import { ARP_PATTERNS, arpeggiator } from '../audio/Arpeggiator';
import { audioContext } from '../audio/context';
import { EFFECT, vss30 } from '../audio/synth';
import { countIn, demoTrack, musicRecorder } from '../audio/transport';
import { midi } from '../midi/MidiBus';
import type { MidiEvent } from '../midi/types';
import { usePanelStore } from './panelStore';
import type { PanelState } from './panelTypes';
import { useKeyboardStore } from './keyboardStore';

const BASE_BPM = 60;
const FILTER_MIN_HZ = 100;
const FILTER_RANGE_HZ = 7600;
const FUZZ_MAKEUP_GAIN = 0.15;

/** Pushes panel state onto the Web Audio graph. */
function applyToSynth(state: PanelState): void {
  const now = audioContext.currentTime;

  musicRecorder.speed = countIn.speed = demoTrack.speed = state.BPM / BASE_BPM;
  arpeggiator.bpm = state.BPM * 5;
  arpeggiator.pattern = ARP_PATTERNS[state.ARP_SELECTION];

  vss30.attack = state.ADSR[0];
  vss30.decay = state.ADSR[1];
  vss30.sustain = state.ADSR[2];
  vss30.release = state.ADSR[3];

  vss30.loop = state.LOOP;
  vss30.loopMode = state.PINGPONG ? 'PINGPONG' : 'NORMAL';
  vss30.loopEnd = state.LOOP_END;

  const echo = vss30.effects[EFFECT.ECHO];
  echo.bypass(!state.ECHO);
  echo.feedback.gain.value = state.REVERB_SIZE * 1.1;

  const fuzz = vss30.effects[EFFECT.FUZZ];
  fuzz.bypass(!state.FUZZ);
  fuzz.output.gain.setTargetAtTime(state.FUZZ ? FUZZ_MAKEUP_GAIN : 1, now, 0.01);

  const filter = vss30.effects[EFFECT.FILTER];
  filter.bypass(!state.FILTER);
  filter.effect.frequency.value = FILTER_MIN_HZ + state.FILTER_CUTOFF * FILTER_RANGE_HZ;

  vss30.effects[EFFECT.REVERB].bypass(!state.REVERB);
}

/**
 * Wires the store, the MIDI bus and the audio graph together.
 * Returns a teardown function; safe to call more than once.
 */
export function installBindings(): () => void {
  applyToSynth(usePanelStore.getState());

  const unsubscribePanel = usePanelStore.subscribe(
    (state) => state as PanelState,
    (state) => applyToSynth(state),
    { fireImmediately: false },
  );

  // Transport position feeds the on-screen progress rings.
  const setTransport = usePanelStore.getState().setTransport;
  const offRecorderTick = musicRecorder.on('tick', ({ ratio }) =>
    setTransport({ MUSIC_RECORDER_POS: ratio }),
  );
  const offDemoTick = demoTrack.on('tick', ({ ratio }) =>
    setTransport({ MUSIC_RECORDER_POS: ratio }),
  );
  const offRecorderRecord = musicRecorder.on('record', (state) =>
    setTransport({ MUSIC_RECORDER_STATE: state }),
  );
  const offRecorderEnd = musicRecorder.on('end', () =>
    setTransport({ MUSIC_RECORDER_STATE: musicRecorder.state }),
  );
  const offDemoEnd = demoTrack.on('end', () => setTransport({ DEMO_STATE: demoTrack.state }));

  // While the arpeggiator is armed it swallows played notes and re-emits its
  // own pattern, so the synth only ever hears one of the two.
  const removeArpProcessor = midi.addProcessor((event) => {
    const { ARP_ENABLED } = usePanelStore.getState();
    if (!ARP_ENABLED || event.source === 'arpeggiator') return event;
    if (event.type === 'noteon') {
      arpeggiator.noteOn(event);
      return null;
    }
    if (event.type === 'noteoff') {
      arpeggiator.noteOff(event);
      return null;
    }
    return event;
  });

  const play = (event: MidiEvent) => {
    vss30.NoteOn(event);
    useKeyboardStore.getState().hold(event.value);
    if (musicRecorder.recording) musicRecorder.addToSchedule(event);
  };

  const release = (event: MidiEvent) => {
    vss30.NoteOff(event);
    useKeyboardStore.getState().release(event.value);
    if (musicRecorder.recording) musicRecorder.addToSchedule(event);
  };

  const unbindNotes = midi.onNoteRange(0, 127, play, release);

  // CC 21 rides the filter fader, matching the original hardware mapping.
  const unbindFilterCC = midi.onControlChange(21, (event) => {
    usePanelStore.getState().setFader('Fader_FilterCutoff', event.ratio);
  });
  const unbindReverbCC = midi.onControlChange(22, (event) => {
    usePanelStore.getState().setFader('Fader_ReverbSize', event.ratio);
  });

  return () => {
    unsubscribePanel();
    offRecorderTick();
    offDemoTick();
    offRecorderRecord();
    offRecorderEnd();
    offDemoEnd();
    removeArpProcessor();
    unbindNotes();
    unbindFilterCC();
    unbindReverbCC();
  };
}
